import { observer } from 'mobx-react';
import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import {
  BrowserRouter,
  Route,
  Switch,
  useHistory,
  useLocation,
} from 'react-router-dom';
import { ParallaxProvider } from 'react-scroll-parallax';
import { CSSTransition } from 'react-transition-group';

import GlobalCSS from './GlobalStyle';
import './Main.css';
import './Slider.css';
import { VFlex } from './components/Flex';
import { HamburgerOverlay } from './components/HamburgerOverlay';
import ScrollToTop from './components/ScrollToTop';
import { cartStore } from './mobx/cart-store';
import { store } from './mobx/store';
import { initializeCart } from './utils/helpers';
import { useUpdateControllerOnRouteChange } from './utils/utils';
import { BASE_URL_NAME } from './utils/constants';
import NotFoundPage from './components/pages/NotFoundPage';
import GoldPricePage from './components/pages/GoldPricePage';
import PriceFetcherUpdateListener from './components/PriceFetcherUpdateListener';
import CrispChat from './components/CrispChat';

const Main = observer(() => {
  const containerRef = useRef();
  const location = useLocation();
  store.location = location;
  useEffect(() => {
    const listener = () => {
      requestAnimationFrame(() => {
        store.setScrollYOffset(window.pageYOffset);
      });
    };
    window.addEventListener('scroll', listener);
    const resizeListener = () => {
      requestAnimationFrame(() => {
        store.setScreenSize({
          width: document.documentElement.clientWidth,
          height: document.documentElement.clientHeight,
        });
      });
    };
    window.addEventListener('resize', resizeListener);
    return () => {
      window.removeEventListener('scroll', listener);
      window.removeEventListener('resize', resizeListener);
    };
  }, []);

  useEffect(() => {
    // set initial value for cart
    initializeCart();
    cartStore.init();
    return () => {
      cartStore && cartStore.deinit();
    };
  }, []);

  const history = useHistory();
  const prevHistoryLength = useRef(history.length);
  useEffect(() => {
    if (
      store.pathnameHistory.length === 0
      || history.length > prevHistoryLength.current
    ) {
      store.pathnameHistory.push(location.pathname);
    } else if (history.length < prevHistoryLength.current) {
      store.pathnameHistory.pop();
    }
    prevHistoryLength.current = history.length;
  }, [location.pathname, history]);
  useUpdateControllerOnRouteChange();
  return (
    <VFlex
      style={{
        width: '100%',
        overflow: 'hidden',
        position: 'relative',
        alignItems: 'center',
      }}
    >
      <VFlex
        style={{
          width: '100%',
          overflow: 'hidden',
          position: 'relative',
          alignItems: 'center',
          background: 'linear-gradient(180deg, #80c9f2 40%, #0E0C34 100%)',
        }}
        ref={containerRef}
      />
    </VFlex>
  );
});

const routes = [
  { path: '/', Component: NotFoundPage, exact: true, strict: true },
  { path: '/:pid', Component: GoldPricePage, exact: true, strict: true },
];
const AnimatedApp = observer(() => {
  return (
    <>
      {routes.map(({ path, Component, ...p }) => {
        return (
          <Switch>
            <Route key={path} path={path} {...p}>
              {({ match }) => {
                console.log('match', match);
                if (
                  match.path === match.url
                  || (match.params && match.params[0])
                  || match.params !== null
                ) {
                  return (
                    <CSSTransition
                      in={match != null}
                      timeout={300}
                      classNames="page"
                      unmountOnExit
                    >
                      <div className="page">
                        <Component path={path} {...p.props} />
                      </div>
                    </CSSTransition>
                  );
                }
                return <NotFoundPage />;
              }}
            </Route>
          </Switch>
        );
      })}
    </>
  );
});

const App = () => {
  const companycontent = BASE_URL_NAME === 'Milways' ? '即市金價 - Milways Gold 萬寶黄金' : '即市金價 - Day Karkal 日羲珠寶';
  return (
    <React.Fragment>
      <GlobalCSS />
      <Helmet>
        <title>
          {companycontent}
        </title>
        <meta
          name="description"
          content={companycontent}
        />
      </Helmet>
      <ParallaxProvider>
        <BrowserRouter>
          <PriceFetcherUpdateListener />
          <ScrollToTop />
          <Main />
          <AnimatedApp />
          {BASE_URL_NAME === 'DayKarkal' && <CrispChat />}
          <HamburgerOverlay />
        </BrowserRouter>
      </ParallaxProvider>
    </React.Fragment>
  );
};

export default App;
